import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  OnDestroy,
  ViewChild,
  ViewChildren,
  QueryList,
  signal,
} from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { FooterComponent } from './footer.component';
import { Utils, locationKeywords, searchKeywords } from './utils';
import { SearchIndexService } from './services/search-index.service';
import { BlogService } from './services/blog.service';
import { BlogListItem } from './blog/blog.models';
import { radii } from './mock-data.component';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { SharedService } from './shared.service';
import { Title } from '@angular/platform-browser';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import {
  HomePageService,
  HomePageData,
  HomeStat,
  HomeHowItWorksStep,
  HomeTestimonial,
  HomeFaqItem,
} from './services/home-page.service';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { JobsNavigationService } from './shared/jobs-navigation.service';
import { BreadcrumbsComponent } from './components/breadcrumbs.component';
import { SearchBoxComponent } from './components/search-box.component';
import { IconComponent } from './icon.component';
import { FeaturedJobsListComponent } from './shared/featured-jobs-list/featured-jobs-list.component';
import { FaqBlockComponent } from './shared/faq-block/faq-block.component';

@Component({
    selector: 'main',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.scss'],
    imports: [
        BreadcrumbsComponent,
        SearchBoxComponent,
        IconComponent,
        FeaturedJobsListComponent,
        FaqBlockComponent,
    ],
})
export class MainComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly mobileTestimonialsBreakpoint = 786;
  @ViewChild(FooterComponent) footerRef: FooterComponent;
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;
  @ViewChildren('testimonialMeasureCard')
  testimonialMeasureCards!: QueryList<ElementRef<HTMLElement>>;

  // CMS Content signals
  homeData = signal<HomePageData | null>(null);
  isLoadingHome = signal<boolean>(true);
  showHomeSkeleton = signal<boolean>(false);
  homeErrorMessage = signal<string | null>(null);

  // Skeleton loading timing
  private skeletonTimeout?: number;
  private readonly skeletonDelayThreshold = 150; // ms before showing skeleton
  private readonly minSkeletonDisplayTime = 400; // ms minimum skeleton display
  private skeletonShownAt = 0;

  private readonly destroy$ = new Subject<void>();

  public radii: number[];
  public form: UntypedFormGroup;
  public isMobileMenuHidden = true;
  public locationAutosuggest: string[] = [];
  public searchKeywordAutosuggest: string[] = [];
  public whatsapp = Utils.whatsapp;
  public whatsAppNumber = Utils.whatsAppNumber;

  // Stats for trust bar - populated from CMS with fallback
  public stats = signal<HomeStat[]>([
    { id: 1, label: 'IT Professionals', value: 250, suffix: '+' },
    { id: 2, label: 'Bedrijven', value: 100, suffix: '+' },
    { id: 3, label: 'Match Score', value: 75, suffix: '%+' },
    { id: 4, label: 'Succesvolle Plaatsingen', value: 150, suffix: '+' },
  ]);

  // Animated stats values for counting animation
  public animatedStats = signal<Array<HomeStat & { displayValue: number }>>([]);
  private statsAnimated = false;

  // How it works steps - populated from CMS with fallback
  public howItWorksSteps = signal<HomeHowItWorksStep[]>([
    {
      id: 1,
      icon: 'search',
      title: 'Zoek',
      description: 'Doorzoek vacatures of laat je profiel matchen',
    },
    {
      id: 2,
      icon: 'person',
      title: 'Connect',
      description: 'Maak contact met de perfecte werkgever',
    },
    {
      id: 3,
      icon: 'done',
      title: 'Start',
      description: 'Begin aan jouw nieuwe IT-uitdaging',
    },
  ]);

  // Featured jobs - will be populated with most recent real jobs
  public featuredJobs: any[] = [];

  // Recent blog articles - will be populated with 3 most recent articles
  public recentBlogs: BlogListItem[] = [];

  // Testimonials - populated from CMS with fallback
  public testimonials = signal<HomeTestimonial[]>([
    {
      id: 1,
      name: 'Joost Thissen',
      role: 'Tech lead / Senior Software Engineer',
      company: 'Ecocharting',
      text: 'Een platform dat écht begrijpt wat wij nodig hebben in IT-talent. Razendsnelle matches, zorgvuldig gescreend en passend bij onze cultuur. Kwaliteit zonder concessies.',
      avatar: 'account_circle',
    },
    {
      id: 2,
      name: 'Andre van der Lingen',
      role: 'DevOps Engineer',
      company: 'RIVM',
      text: 'Top bedrijf, ze denken met je mee, in alle situaties. Goede geprekken, leuke en oprecht geïnteresseerde mensen. Complimenten en veel succes voor de toekomst. ',
      avatar: 'account_circle',
    },
    {
      id: 3,
      name: 'Richy Desconocido',
      role: 'Infra Engineer',
      company: '',
      text: 'Blauwtand-IT heeft me uitstekend geholpen! Ik ben ontzettend blij met hun begeleiding bij het vinden van een functie als Infra Engineer die perfect bij me past. Dankzij hun inzet heb ik inmiddels een vast contract bij een bedrijf waar zowel de structuur als de werkzaamheden naadloos aansluiten bij mijn wensen. Ik wil Blauwtand-IT dan ook oprecht bedanken voor de fijne samenwerking en het vinden van deze ideale match. Een absolute aanrader voor iedereen die op zoek is naar de juiste stap in de IT!',
      avatar: 'account_circle',
    },
    {
      id: 4,
      name: 'Jake Kranenberg',
      role: '.NET developer',
      company: 'Cegeka',
      text: 'Uitstekend begeleid door een recruiter (Wesley) van Blauwtand. Er werd de tijd genomen om mij te leren kennen en hij heeft duidelijk verstand van zaken. Ook werd ik netjes op de hoogte gehouden en verliepen de gesprekken vlotjes. Blij met mijn nieuwe uitdaging en de coaching vanuit Blauwtand.',
      avatar: 'account_circle',
    },
    {
      id: 5,
      name: 'Yoshi Knotter',
      role: 'Test automation consultant',
      company: 'a.s.r. verzekeringen',
      text: 'Goed advies gekregen betreffende mijn carrière, zeker een aanrader!',
      avatar: 'account_circle',
    },
    {
      id: 6,
      name: 'Ruslan Hassanov',
      role: 'Site Reliability Engineer',
      company: 'ans',
      text: 'Hele fijne, professionele samenwerking met een persoonlijke toets. Zeker aan te raden.',
      avatar: 'account_circle',
    },
    {
      id: 7,
      name: 'Lisa Eijgendaal',
      role: 'Head of People & Culture',
      company: 'MyWheels',
      text: 'Fijn contact, ze denken goed met je mee.',
      avatar: 'account_circle',
    },
    {
      id: 8,
      name: 'Gerben Dir',
      role: 'Senior Developer',
      company: '',
      text: 'Mijn ervaring met Blauwtand IT is heel positief. Wat hen onderscheidt, is hun persoonlijke benadering. Ze kijken verder dan alleen het cv en verdiepen zich echt in de persoon achter de professional. Tijdens mijn samenwerking met hen merkte ik dat ze niet alleen focussen op de huidige opdracht, maar ook kijken naar mijn toekomstige ambities. Hun team is deskundig, betrokken en altijd bereid om mee te denken. Dankzij hun persoonlijke aanpak voel ik me geen nummer, maar een gewaardeerde professional. Ik kan Blauwtand IT van harte aanbevelen aan iedereen die op zoek is naar een betrouwbare en vooruitstrevende IT-partner.',
      avatar: 'account_circle',
    },
  ]);

  // FAQ items - populated from CMS with fallback
  public faqItems = signal<(HomeFaqItem & { expanded: boolean })[]>([
    {
      id: 1,
      question: 'Hoe werkt Blauwtand?',
      answer:
        'Blauwtand verbindt IT-professionals met bedrijven door middel van slimme matching en persoonlijk contact. Je kunt vacatures zoeken of je profiel laten matchen met openstaande posities.',
      expanded: false,
    },
    {
      id: 2,
      question: 'Zijn jullie diensten gratis voor professionals?',
      answer:
        'Ja, voor IT-professionals zijn al onze diensten volledig gratis. We worden betaald door bedrijven wanneer we een succesvolle match maken.',
      expanded: false,
    },
    {
      id: 3,
      question: 'Hoelang duurt het voordat ik reactie krijg?',
      answer:
        'Gemiddeld krijg je binnen 24-48 uur reactie op je sollicitatie. Voor urgente vacatures vaak nog sneller.',
      expanded: false,
    },
    {
      id: 4,
      question: 'Werken jullie ook met freelancers?',
      answer:
        'Ja, we bemiddelen zowel voor vaste posities als opdrachten. We hebben een breed netwerk van bedrijven met verschillende samenwerkingsvormen.',
      expanded: false,
    },
    {
      id: 5,
      question: "In welke regio's zijn jullie actief?",
      answer:
        'We zijn actief in heel Nederland. Ook remote-posities behoren tot de mogelijkheden.',
      expanded: false,
    },
  ]);

  public currentTestimonialIndex = 0;
  public testimonialCardMinHeight = signal<number | null>(null);

  public get profession(): string | null {
    return this.form.get('profession')?.value;
  }
  public get location(): string | null {
    return this.form.get('location')?.value;
  }
  public get radius(): number | null {
    return this.form.get('radius')?.value;
  }

  public constructor(
    private readonly elementRef: ElementRef,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly formBuilder: UntypedFormBuilder,
    private sharedService: SharedService,
    private titleService: Title,
    private readonly searchIndex: SearchIndexService,
    private readonly httpClient: HttpClient,
    private readonly blogService: BlogService,
    private readonly homePageService: HomePageService,
    private readonly cdr: ChangeDetectorRef,
    private readonly jobsNavigation: JobsNavigationService,
  ) {
    this.radii = radii;
  }

  public get searchHistory(): string[] {
    if (localStorage.getItem('history')) {
      const history = JSON.parse(localStorage.getItem('history') ?? '');
      const profession = (history?.profession as Array<string>) ?? [];
      if (profession?.[0] === '') {
        return [];
      }
      return profession;
    }
    return [];
  }

  public ngOnInit(): void {
    window.scrollTo(0, 0);
    this.titleService.setTitle('Blauwtand');

    // Load home page content from CMS
    this.loadHomePageData();

    // Ensure suggestions work on the home page before vacancies are loaded
    this.searchIndex.initialize([], searchKeywords, locationKeywords);

    // Fetch real jobs for featured section
    this.httpClient
      .get<{ jobs: any[] }>(`${Utils.getApiUrl()}/vacancies`, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      })
      .subscribe({
        next: (res) => {
          const jobs = res?.jobs ?? [];
          this.searchIndex.initialize(
            jobs as any,
            searchKeywords,
            locationKeywords
          );

          // Get the most recent jobs for featured section (number from CMS or default 6)
          const featuredLimit = this.homeData()?.featuredJobsLimit ?? 6;
          const sortedJobs = [...jobs].sort((a: any, b: any) => {
            const dateA = new Date(a.date || a.createdAt || 0).getTime();
            const dateB = new Date(b.date || b.createdAt || 0).getTime();
            return dateB - dateA; // descending order (newest first)
          });
          this.featuredJobs = sortedJobs.slice(0, featuredLimit);
          this.cdr.markForCheck();
        },
        error: () => {
          /* keep static suggestions */
        },
      });

    // Fetch real blog articles for blog preview section
    this.blogService.list({ limit: 3 }).subscribe({
      next: (response) => {
        this.recentBlogs = response.items ?? [];
        this.cdr.markForCheck();
      },
      error: () => {
        this.recentBlogs = []; // Keep empty if fetch fails
        this.cdr.markForCheck();
      },
    });

    this.form = this.formBuilder.group({
      profession: this.formBuilder.control(
        this.sharedService?.filterData?.profession ?? ''
      ),
      location: this.formBuilder.control(
        this.sharedService?.filterData?.location ?? ''
      ),
      radius: this.formBuilder.control(0),
    });
    this.form.get('location')?.valueChanges.subscribe((i: string) => {
      this.locationAutosuggest = [];
      if (i.trim().toLowerCase() === '') {
        return;
      }

      for (const a of locationKeywords) {
        const r = a.slice(0, i.length).toLowerCase();
        if (i.toLowerCase().includes(r)) {
          this.locationAutosuggest.push(a);
        }
      }
    });
    this.form.get('profession')?.valueChanges.subscribe((i: string) => {
      this.searchKeywordAutosuggest = [];
      if (i.trim().toLowerCase() === '') {
        return;
      }

      for (const a of searchKeywords) {
        const r = a.slice(0, i.length).toLowerCase();
        if (i.toLowerCase().includes(r)) {
          this.searchKeywordAutosuggest.push(a);
        }
      }
    });
  }

  public async ngAfterViewInit(): Promise<void> {
    this.testimonialMeasureCards.changes
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.scheduleTestimonialHeightMeasurement();
      });

    await Utils.timeout();
    window.addEventListener('scroll', () => {
      this.scrollFade();
    });
    window.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        this.search();
        this.autocomplete?.closePanel?.();
      }
    });
    this.scrollFade();
    this.scheduleTestimonialHeightMeasurement();
  }

  public ngOnDestroy(): void {
    if (this.skeletonTimeout) {
      clearTimeout(this.skeletonTimeout);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    this.scheduleTestimonialHeightMeasurement();
  }

  /**
   * Load home page content from Strapi CMS
   */
  private loadHomePageData(): void {
    this.isLoadingHome.set(true);
    this.homeErrorMessage.set(null);

    // Start delayed skeleton timer
    this.skeletonTimeout = window.setTimeout(() => {
      this.showHomeSkeleton.set(true);
      this.skeletonShownAt = Date.now();
    }, this.skeletonDelayThreshold);

    this.homePageService
      .getHomePage()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => this.handleHomeDataLoaded(data),
        error: (error) => {
          console.error('Failed to load home page:', error);
          this.handleHomeLoadError();
        },
      });
  }

  /**
   * Handle successful home page data load with smart skeleton timing
   */
  private handleHomeDataLoaded(data: HomePageData | null): void {
    if (this.skeletonTimeout) {
      clearTimeout(this.skeletonTimeout);
      this.skeletonTimeout = undefined;
    }

    const updateContent = () => {
      // CRITICAL: Set data FIRST, then turn off skeleton
      if (data) {
        this.homeData.set(data);

        // Update content from CMS
        if (data.stats && data.stats.length > 0) {
          this.stats.set(data.stats);
        }
        if (data.howItWorksSteps && data.howItWorksSteps.length > 0) {
          this.howItWorksSteps.set(data.howItWorksSteps);
        }
        if (data.testimonials && data.testimonials.length > 0) {
          this.testimonials.set(data.testimonials);
        }
        if (data.faqItems && data.faqItems.length > 0) {
          // Add expanded property to FAQ items
          this.faqItems.set(
            data.faqItems.map((item) => ({ ...item, expanded: false }))
          );
        }

        // Update SEO if provided
        if (data.seoTitle) {
          this.titleService.setTitle(data.seoTitle);
        }
      }

      this.isLoadingHome.set(false);
      // Re-check section visibility so stats animation can start with CMS values.
      this.scrollFade();

      // Turn off skeleton AFTER data is set (next tick to ensure render)
      setTimeout(() => {
        this.showHomeSkeleton.set(false);
        this.scheduleTestimonialHeightMeasurement();
      }, 0);
    };

    // If skeleton is visible, ensure minimum display time
    if (this.showHomeSkeleton()) {
      const elapsed = Date.now() - this.skeletonShownAt;
      const remaining = Math.max(0, this.minSkeletonDisplayTime - elapsed);

      setTimeout(() => {
        updateContent();
      }, remaining);
    } else {
      // Fast load: show content immediately
      updateContent();
    }
  }

  /**
   * Handle home page load error with smart skeleton timing
   */
  private handleHomeLoadError(): void {
    if (this.skeletonTimeout) {
      clearTimeout(this.skeletonTimeout);
      this.skeletonTimeout = undefined;
    }

    const updateError = () => {
      // Keep fallback content, just mark as not loading
      this.isLoadingHome.set(false);
      // Re-check section visibility now loading has completed.
      this.scrollFade();
      // Don't set error message - just use fallback content silently

      // Turn off skeleton AFTER error is handled
      setTimeout(() => {
        this.showHomeSkeleton.set(false);
        this.scheduleTestimonialHeightMeasurement();
      }, 0);
    };

    // If skeleton is showing, maintain minimum display time
    if (this.showHomeSkeleton()) {
      const elapsed = Date.now() - this.skeletonShownAt;
      const remaining = Math.max(0, this.minSkeletonDisplayTime - elapsed);

      setTimeout(() => {
        updateError();
      }, remaining);
    } else {
      updateError();
    }
  }

  public clearProfession(): void {
    this.form.patchValue({ profession: '' });
  }

  public clearLocation(): void {
    this.form.patchValue({ location: '' });
  }

  public showDivider(index: number): boolean {
    return index < this.searchHistory.length - 1;
  }

  public setProfession(word: string): void {
    this.form.patchValue({ profession: word });
  }

  public search(): void {
    const profession = (this.profession ?? '').trim();
    const location = (this.location ?? '').trim();

    this.sharedService.filterData.profession = profession;
    this.sharedService.filterData.location = location;
    Utils.saveSearchHistory(profession);

    this.router.navigate(['/vacatures'], {
      queryParams: {
        q: profession || null,
        location: location || null,
      },
    });
  }

  public toggleMobileMenu(): void {
    this.isMobileMenuHidden = !this.isMobileMenuHidden;
    document.body.style.position = this.isMobileMenuHidden ? '' : 'fixed';
  }

  public routeTo(menuItem: string, isMobileMenu: boolean = false): void {
    this.router.navigate([`${menuItem.toLowerCase()}`], {
      relativeTo: this.activatedRoute,
    });
  }

  public routeToBlog(slug: string): void {
    this.router.navigate(['stories', slug]);
  }

  public viewJob(job: any): void {
    this.jobsNavigation.navigateToJob(job);
  }

  public getEmploymentType(job: any): string {
    // Return employment type or default to 'Vast'
    return job.employment || 'Vast';
  }

  public getJobLocation(job: any): string {
    return this.jobsNavigation.getJobLocation(job);
  }

  public getCompanyName(job: any): string {
    // Extract company name from recruiter or use a default
    if (job.recruiter && job.recruiter.name) {
      return job.recruiter.name;
    }
    return 'Vertrouwelijk';
  }


  public nextTestimonial(): void {
    this.currentTestimonialIndex =
      (this.currentTestimonialIndex + 1) % this.testimonials().length;
  }

  public prevTestimonial(): void {
    this.currentTestimonialIndex =
      this.currentTestimonialIndex === 0
        ? this.testimonials().length - 1
        : this.currentTestimonialIndex - 1;
  }

  private scheduleTestimonialHeightMeasurement(): void {
    setTimeout(() => {
      if (window.innerWidth > this.mobileTestimonialsBreakpoint) {
        this.testimonialCardMinHeight.set(null);
        return;
      }

      const cards = this.testimonialMeasureCards?.toArray() ?? [];
      if (!cards.length) {
        this.testimonialCardMinHeight.set(null);
        return;
      }

      const tallestCard = Math.max(
        ...cards.map((card) => card.nativeElement.offsetHeight),
      );

      this.testimonialCardMinHeight.set(tallestCard || null);
    }, 0);
  }

  public getAnimatedStatDisplayValue(index: number): number {
    return this.animatedStats()[index]?.displayValue ?? this.stats()[index]?.value ?? 0;
  }

  private scrollFade(): void {
    const elements = (
      this.elementRef.nativeElement as HTMLElement
    ).querySelectorAll('.animate-on-scroll:not(.has-animated)');

    elements.forEach((element) => {
      const rect = element.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight - 100;

      if (isVisible && element.classList.contains('animate-hidden')) {
        element.classList.remove('animate-hidden');
        element.classList.add('animate-visible');

        // Mark as animated to prevent re-triggering
        element.classList.add('has-animated');

      }
    });

    // Handle stats animation independently from section "has-animated" state,
    // so late CMS data still animates correctly when the section is already visible.
    const statsSection = (
      this.elementRef.nativeElement as HTMLElement
    ).querySelector('.value-prop-stats-section') as HTMLElement | null;
    if (statsSection && !this.statsAnimated && !this.isLoadingHome()) {
      const statsVisible = statsSection.getBoundingClientRect().top < window.innerHeight - 100;
      if (statsVisible) {
        this.animateStats();
        this.statsAnimated = true;
      }
    }

    // Keep legacy support for .scroll-fade
    const connecting = (
      this.elementRef.nativeElement as HTMLElement
    ).querySelector('.connecting');

    if (
      connecting &&
      connecting.getBoundingClientRect().y < window.innerHeight + 300 &&
      connecting.classList.contains('scroll-fade')
    ) {
      connecting!.classList.remove('scroll-fade');
    }
  }

  private animateStats(): void {
    const durationMs = 2000; // 2 seconds
    const startAt = performance.now();

    // Snapshot targets once so values don't drift if stats() updates mid-animation.
    const targets = this.stats();

    // Track previous values so rounding never causes visible back-and-forth.
    const previous: number[] = targets.map(() => 0);

    // Initialize animated stats with 0 values
    this.animatedStats.set(targets.map((stat) => ({ ...stat, displayValue: 0 })));

    const tick = (now: number) => {
      const elapsed = now - startAt;
      const progress = Math.min(Math.max(elapsed / durationMs, 0), 1);
      const eased = this.easeOutQuart(progress);

      const animated = targets.map((stat, idx) => {
        const target = stat.value;

        // Round rather than floor for a more even distribution of increments.
        // Clamp + monotonic guard so the number only moves forward and never overshoots.
        const rounded = Math.round(target * eased);
        const next = Math.min(target, Math.max(previous[idx], rounded));
        previous[idx] = next;

        return { ...stat, displayValue: next };
      });

      this.animatedStats.set(animated);

      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);
  }

  private easeOutQuart(t: number): number {
    return 1 - Math.pow(1 - t, 4);
  }
}
